/**
 * LES MENTIONS DU CARTOUCHE, SELON LA SITUATION — copie d'écran du module serveur.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * ⚠️ MIROIR de `backend/src/mentions-cartouche.ts`. Tout ce qui suit la présente
 * en-tête y est IDENTIQUE, au caractère près, et
 * `backend/tests/mentions-cartouche.test.js` échoue si les deux divergent.
 * Modifier l'un, c'est recopier l'autre — rien d'autre ne les tient ensemble.
 *
 * Pourquoi une copie plutôt qu'un import : le serveur est un projet TypeScript
 * autonome, l'application Next en est un autre, et `shared/sync.sh` ne dessert
 * que les trois applications d'écran (site, manuel, ged) — pas le serveur.
 * C'est le même arrangement que `etatApercu()` / `lib/meta.ts`, avec le même
 * garde-fou : un contrôle qui lit les deux sources.
 *
 * Ici, cette copie ne sert qu'à l'APERÇU en direct de l'écran de paramétrage.
 * Ce qui part sur le papier est toujours calculé par le serveur.
 */

/** Les quatre situations où la plateforme écrit une phrase sur le document. */
export type SituationMention = 'copieMaitrisee' | 'copieNonMaitrisee' | 'enValidation' | 'nonApplicable';

export const SITUATIONS: SituationMention[] = ['copieMaitrisee', 'copieNonMaitrisee', 'enValidation', 'nonApplicable'];

/** Ce qui s'imprime tant que le laboratoire n'a rien réécrit. Ces textes sont
 *  ceux qui circulaient avant le 06/10/2026 : le paramétrage ne change RIEN
 *  tant qu'on n'y touche pas. */
export const MENTIONS_DEFAUT: Record<SituationMention, string> = {
  copieMaitrisee: 'COPIE MAÎTRISÉE n° {numero} — {lieu} — imprimée le {date} par {par}',
  copieNonMaitrisee: 'COPIE NON MAÎTRISÉE — éditée le {date} par {par}',
  enValidation: 'EN COURS DE VALIDATION — NE PAS APPLIQUER',
  nonApplicable: 'NE PAS APPLIQUER',
};

/** Où chaque mention apparaît, et quand — le texte de l'écran de paramétrage. */
export const SITUATION_LIBELLE: Record<SituationMention, { titre: string; quand: string }> = {
  copieMaitrisee: {
    titre: 'Copie maîtrisée',
    quand: 'Pied de page, en vert. Exemplaire papier déclaré au registre : on sait lequel remplacer, et où.',
  },
  copieNonMaitrisee: {
    titre: 'Copie non maîtrisée',
    quand: 'Pied de page, en rouge. Tout fichier qui sort de la plateforme sans être déclaré au registre.',
  },
  enValidation: {
    titre: 'En cours de validation',
    quand: 'En diagonale, sur toutes les pages. Document à vérifier ou à approuver : il se lit, il ne s’applique pas.',
  },
  nonApplicable: {
    titre: 'Non applicable',
    quand: 'En diagonale, sur toutes les pages. Version obsolète ou document retiré d’urgence.',
  },
};

/** Les jetons remplaçables. `situations` dit où le jeton a une valeur : annoncer
 *  `{numero}` sur un filigrane de version obsolète n'aurait aucun sens. */
export const JETONS: { cle: string; libelle: string; situations: SituationMention[] }[] = [
  { cle: 'date', libelle: 'Date d’édition (aujourd’hui)', situations: SITUATIONS },
  { cle: 'par', libelle: 'Qui édite le document', situations: SITUATIONS },
  { cle: 'code', libelle: 'Référence du document', situations: SITUATIONS },
  { cle: 'titre', libelle: 'Titre du document', situations: SITUATIONS },
  { cle: 'version', libelle: 'Numéro de version', situations: SITUATIONS },
  { cle: 'statut', libelle: 'Statut (En vigueur, Obsolète…)', situations: SITUATIONS },
  { cle: 'labo', libelle: 'Nom du laboratoire', situations: SITUATIONS },
  { cle: 'dateVigueur', libelle: 'Date de mise en vigueur', situations: SITUATIONS },
  { cle: 'numero', libelle: 'N° de l’exemplaire au registre', situations: ['copieMaitrisee'] },
  { cle: 'lieu', libelle: 'Lieu d’affichage de l’exemplaire', situations: ['copieMaitrisee'] },
];

export const CLES_JETONS: string[] = JETONS.map((j) => j.cle);

export type ValeursMention = Partial<Record<string, string | number | null | undefined>>;

/** Les jetons écrits dans un modèle, dans l'ordre, sans doublon. */
export function jetonsDe(modele: string): string[] {
  return [...new Set([...String(modele || '').matchAll(/\{([A-Za-zÀ-ÿ0-9_]+)\}/g)].map((m) => String(m[1])))];
}

/** Ceux que la plateforme ne sait pas remplir — ce que l'écran signale. */
export function jetonsInconnus(modele: string, situation?: SituationMention): string[] {
  return jetonsDe(modele).filter((j) => {
    const connu = JETONS.find((x) => x.cle === j);
    return !connu || (situation ? !connu.situations.includes(situation) : false);
  });
}

/**
 * Remplace les jetons, puis recoud la phrase.
 *
 * Le nettoyage est le cœur du travail : une fois `{lieu}` remplacé par du vide,
 * il reste un tiret cadratin sans rien autour. On supprime les séparateurs
 * devenus orphelins — à l'intérieur comme aux deux bouts.
 */
export function remplirMention(modele: string, valeurs: ValeursMention = {}): string {
  const brut = String(modele || '').replace(/\{([A-Za-zÀ-ÿ0-9_]+)\}/g, (_m, cle: string) => {
    const v = valeurs[cle];
    return v === null || v === undefined ? '' : String(v).trim();
  });
  return brut
    // deux séparateurs qui se suivent : celui du milieu a perdu son contenu
    .replace(/\s*([—·|/])(\s*[—·|/])+\s*/g, ' $1 ')
    // un séparateur en tête ou en queue ne sépare plus rien
    .replace(/^[\s—·|]+/, '')
    .replace(/[\s—·|]+$/, '')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

/**
 * La mention à imprimer : le modèle du laboratoire s'il en a écrit un et qu'il
 * ne se vide pas une fois rempli, celui d'origine sinon.
 *
 * Le second repli n'est pas de la prudence gratuite : « {numero} » seul, sur un
 * document dont l'exemplaire n'a pas de numéro, donne une mention VIDE. Un pied
 * de page muet est précisément ce que la mention existe pour empêcher.
 */
export function mentionDe(
  mentions: Partial<Record<SituationMention, string>> | null | undefined,
  situation: SituationMention,
  valeurs: ValeursMention = {},
): string {
  const choisi = String((mentions || {})[situation] || '').trim();
  const rendu = choisi ? remplirMention(choisi, valeurs) : '';
  return rendu || remplirMention(MENTIONS_DEFAUT[situation], valeurs) || MENTIONS_DEFAUT[situation];
}
